import React, { useState } from "react";
import { jsPDF } from "jspdf";
import { api, formatApiError, inr } from "@/lib/api";
import { loadLogo } from "@/lib/payslip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Eye, Printer, DownloadSimple, EnvelopeSimple } from "@phosphor-icons/react";

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const two = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]} ${ONES[n % 10]}`.trim());
const three = (n) => [n >= 100 && `${ONES[Math.floor(n / 100)]} Hundred`, two(n % 100)].filter(Boolean).join(" ");
export const inWords = (num) => {
  let n = Math.round(Number(num) || 0);
  if (!n) return "Zero";
  const parts = [];
  [[10000000, "Crore"], [100000, "Lakh"], [1000, "Thousand"]].forEach(([d, w]) => {
    const q = Math.floor(n / d);
    if (q) { parts.push(`${q >= 100 ? inWords(q) : two(q)} ${w}`); n %= d; }
  });
  if (n) parts.push(three(n));
  return parts.join(" ");
};
const ord = (d) => (d % 10 === 1 && d !== 11 ? "st" : d % 10 === 2 && d !== 12 ? "nd" : d % 10 === 3 && d !== 13 ? "rd" : "th");
export const longDate = (v) => {
  const d = v ? new Date(v) : null;
  if (!d || isNaN(d)) return v || "";
  return `${d.getDate()}${ord(d.getDate())} ${d.toLocaleString("en-GB", { month: "long" })}, ${d.getFullYear()}`;
};
const NUM_WORD = { 1: "one", 2: "two", 3: "three", 4: "four", 5: "five", 6: "six", 7: "seven", 8: "eight", 9: "nine", 10: "ten", 11: "eleven", 12: "twelve" };

const TERMS = (co) => [
  ["Working Hours", "Your working hours shall be as per the Company's rules and may be revised from time to time depending upon business requirements."],
  ["Leave & Benefits", "You will be eligible for leave, holidays, and statutory benefits including EPF, ESI, Bonus, Gratuity, and other benefits, wherever applicable, in accordance with Company policy and applicable Indian labour laws."],
  ["Confidentiality", "You shall maintain strict confidentiality regarding all information, documents, customer data, designs, software, pricing, and trade secrets belonging to the Company during and after your employment."],
  ["Termination", "Either party may terminate the employment by giving 30 days' written notice or salary in lieu of notice, subject to Company policy and applicable law."],
];
const CONDITIONS = [
  "You shall faithfully perform the duties assigned to you by the Company.",
  "You shall comply with all Company rules, regulations, and policies.",
  "You shall maintain confidentiality of all Company information.",
  "Any misconduct, fraud, breach of confidentiality, or violation of Company policy may lead to disciplinary action, including termination of employment.",
  "Your employment shall continue subject to satisfactory performance and compliance with Company policies.",
  "Statutory benefits shall be provided wherever applicable under Indian law.",
];

export const drawLetterHead = async (doc, c) => {
  const X = 20, R = 190;
  const logo = await loadLogo(c.logo_url || "/brand/vetrich-logo-pdf.jpg");
  if (logo) { try { doc.addImage(logo, /^data:image\/jpe?g/.test(logo) ? "JPEG" : "PNG", X, 12, 30, 26, undefined, "FAST"); } catch { /* unsupported image */ } }
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text(c.name || "", R, 18, { align: "right" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5);
  const addr = [c.address, c.city, c.state, c.pincode].filter((v, i) => v && (i === 0 || !(c.address || "").toLowerCase().includes(String(v).toLowerCase()))).join(", ");
  let hy = 23;
  if (addr) { doc.splitTextToSize(`Address: ${addr}`, 95).forEach((l) => { doc.text(l, R, hy, { align: "right" }); hy += 4.5; }); }
  const contact = [c.phone && `Phone: ${c.phone}`, c.email && `Email: ${c.email}`].filter(Boolean).join(" | ");
  if (contact) { doc.text(contact, R, hy, { align: "right" }); hy += 4.5; }
  if (c.gstin) { doc.text(`GSTIN: ${c.gstin}`, R, hy, { align: "right" }); }
};

export const drawFooter = (doc, c) => {
  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(120);
    doc.text(`${c.name || ""}. All Rights Reserved.`, 105, 290, { align: "center" });
    doc.setTextColor(0);
  }
};

export const buildLetterPdf = async (L) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const c = L.company || {};
  const co = c.name || "the Company";
  const X = 20, W = 170, R = 190;
  let y = 20;
  const page = (need) => { if (y + need > 280) { doc.addPage(); y = 20; } };
  const para = (t, gap = 4) => {
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    const lines = doc.splitTextToSize(t, W);
    page(lines.length * 5);
    doc.text(lines, X, y); y += lines.length * 5 + gap;
  };
  const head = (t) => { page(24); y += 2; doc.setFont("helvetica", "bold"); doc.setFontSize(11.5); doc.text(t, X, y); y += 7; };

  await drawLetterHead(doc, c);
  y = 50;
  doc.setFont("helvetica", "bold"); doc.setFontSize(15);
  doc.text("OFFER LETTER", 105, y, { align: "center" }); y += 12;
  para(`Date: ${longDate(L.created_at)}`, 2);
  para(`Offer Letter No.: ${L.letter_no}`, 6);
  para("To,", 2);
  para(`Mr./Ms. ${L.employee_name}`, 2);
  if (L.employee_address) para(`Address: ${L.employee_address}`, 2);
  y += 4;
  head("Subject: Offer of Employment");
  para(`Dear Mr./Ms. ${L.employee_name},`);
  para(`We are pleased to offer you the position of ${L.designation || "a member of our team"} with ${co}.`);
  para(`Your appointment will be effective from ${longDate(L.joining_date)}, subject to your acceptance of the terms and conditions mentioned below.`);

  head("Employment Details");
  const rows = [
    ["Employee Name:", L.employee_name], ["Employee ID:", L.employee_code], ["Designation:", L.designation],
    ["Department:", L.department], ["Employment Type:", L.employment_type], ["Date of Joining:", longDate(L.joining_date)],
    ["Place of Posting:", L.work_location], ["Reporting To:", L.reporting_to],
  ].filter(([, v]) => v);
  page(rows.length * 9);
  doc.setDrawColor(210); doc.setLineWidth(0.2);
  rows.forEach(([k, v]) => {
    doc.rect(X, y, 70, 9); doc.rect(X + 70, y, W - 70, 9);
    doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.text(k, X + 3, y + 6);
    doc.setFont("helvetica", "normal"); doc.text(String(v), X + 73, y + 6);
    y += 9;
  });
  y += 6;

  const ctc = Math.round((Number(L.monthly_salary) || 0) * 12);
  head("Compensation");
  para(`Your Annual Cost to Company (CTC) shall be Rs. ${ctc.toLocaleString("en-IN")} (Rupees ${inWords(ctc)} Only) per annum.`);
  para("Your salary shall be paid monthly after deduction of applicable taxes and statutory deductions in accordance with the Company's policies and applicable laws.");
  const pm = Number(L.probation_months) || 0;
  if (pm > 0) {
    head("Probation");
    para(`You will be on probation for a period of ${NUM_WORD[pm] || pm} (${pm}) month${pm > 1 ? "s" : ""} from the date of joining. Your employment may be confirmed upon satisfactory performance.`);
  }
  TERMS(co).forEach(([h, t]) => { head(h); para(t); });
  head("Conditions of Employment");
  [...CONDITIONS, ...(L.notes ? [L.notes] : [])].forEach((t) => para(`- ${t}`, 1.5));
  y += 6;
  para(`We welcome you to ${co} and wish you a successful career with us.`, 10);
  page(30);
  doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text(`For ${c.name || ""}`, X, y); y += 18;
  doc.text("Authorized Signatory", X, y);
  drawFooter(doc, c);
  return doc;
};

const fileName = (L) => `Offer_Letter_${L.employee_name.replace(/\W+/g, "_")}_${L.letter_no.replace(/\//g, "-")}.pdf`;

export const LetterActions = ({ letter, empEmail, onSent }) => {
  const [sending, setSending] = useState(false);
  const open = async (print) => {
    const doc = await buildLetterPdf(letter);
    if (print) doc.autoPrint();
    window.open(doc.output("bloburl"), "_blank", "noopener");
  };
  const email = async () => {
    const to = window.prompt("Send joining letter to email:", empEmail || "");
    if (!to?.trim()) return;
    setSending(true);
    try {
      const pdf_base64 = (await buildLetterPdf(letter)).output("datauristring").split(",")[1];
      await api.post(`/hr/letters/${letter.id}/email`, { to: to.trim(), pdf_base64 });
      toast.success(`Joining letter emailed to ${to.trim()}`);
      onSent && onSent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };
  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" disabled={sending} onClick={email} data-testid={`hr-letter-email-${letter.id}`}><EnvelopeSimple size={13} className="mr-1" />{sending ? "Sending…" : "Email"}</Button>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => open(false)} data-testid={`hr-letter-preview-${letter.id}`}><Eye size={13} className="mr-1" />Preview</Button>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={async () => (await buildLetterPdf(letter)).save(fileName(letter))} data-testid={`hr-letter-download-${letter.id}`}><DownloadSimple size={13} className="mr-1" />PDF</Button>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => open(true)} data-testid={`hr-letter-print-${letter.id}`}><Printer size={13} className="mr-1" />Print</Button>
    </div>
  );
};

export const GenerateLetterButton = ({ emp, canGenerate, blockers, onDone }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const fresh = () => ({ joining_date: emp.joining_date || "", designation: emp.designation || "", work_location: emp.work_location || "", reporting_to: emp.reporting_manager || "", probation_months: 6, notes: "", employee_address: emp.address || "" });
  const [f, setF] = useState(fresh);
  const openDlg = () => { setF(fresh()); setOpen(true); };

  const submit = async () => {
    if (!f.joining_date) { toast.error("Joining date is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/hr/employees/${emp.id}/joining-letter`, { ...f, probation_months: Number(f.probation_months) || 0 });
      toast.success(`Joining Letter ${data.letter_no} saved to employee profile`);
      setOpen(false); onDone();
      window.open((await buildLetterPdf(data)).output("bloburl"), "_blank", "noopener");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <>
      <Button className="h-9" disabled={!canGenerate} onClick={openDlg}
        title={canGenerate ? "" : `Blocked: ${blockers.join("; ")}`} data-testid="hr-generate-letter">
        <FileText size={14} className="mr-1.5" /> Generate Joining Letter
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="hr-letter-dialog">
          <DialogHeader><DialogTitle>Joining Letter — {emp.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="eyebrow">Joining Date *</Label><Input type="date" value={f.joining_date} onChange={set("joining_date")} data-testid="hr-letter-date" /></div>
            <div className="space-y-1"><Label className="eyebrow">Designation</Label><Input value={f.designation} onChange={set("designation")} data-testid="hr-letter-designation" /></div>
            <div className="space-y-1"><Label className="eyebrow">Monthly Salary</Label><Input value={inr(emp.monthly_salary)} disabled data-testid="hr-letter-salary" /></div>
            <div className="space-y-1"><Label className="eyebrow">Probation (months)</Label><Input type="number" value={f.probation_months} onChange={set("probation_months")} data-testid="hr-letter-probation" /></div>
            <div className="space-y-1"><Label className="eyebrow">Work Location</Label><Input value={f.work_location} onChange={set("work_location")} data-testid="hr-letter-location" /></div>
            <div className="space-y-1"><Label className="eyebrow">Reporting To</Label><Input value={f.reporting_to} onChange={set("reporting_to")} data-testid="hr-letter-reporting" /></div>
            <div className="space-y-1 col-span-2"><Label className="eyebrow">Employee Address</Label><Input value={f.employee_address} onChange={set("employee_address")} data-testid="hr-letter-address" /></div>
            <div className="space-y-1 col-span-2"><Label className="eyebrow">Additional Terms</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="hr-letter-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="hr-letter-submit">Generate & Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
